"use client";

import React, { useEffect, useState } from 'react';
import { MapPin, Navigation, ArrowRight, CircleCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface Suburb {
  id: string;
  name: string;
  x: number;
  y: number;
  priority: boolean;
}
const SUBURBS: Suburb[] = [{
  id: 'swan-hill',
  name: 'Swan Hill (HQ)',
  x: 50,
  y: 55,
  priority: true
}, {
  id: 'melbourne',
  name: 'Melbourne',
  x: 52,
  y: 75,
  priority: true
}, {
  id: 'sydney',
  name: 'Sydney',
  x: 75,
  y: 65,
  priority: true
}, {
  id: 'adelaide',
  name: 'Adelaide',
  x: 35,
  y: 68,
  priority: true
}, {
  id: 'mildura',
  name: 'Mildura',
  x: 42,
  y: 50,
  priority: true
}, {
  id: 'bendigo',
  name: 'Bendigo',
  x: 48,
  y: 68,
  priority: true
}, {
  id: 'shepparton',
  name: 'Shepparton',
  x: 55,
  y: 65,
  priority: true
}, {
  id: 'echuca',
  name: 'Echuca',
  x: 52,
  y: 62,
  priority: true
}, {
  id: 'brisbane',
  name: 'Brisbane',
  x: 85,
  y: 45,
  priority: false
}, {
  id: 'perth',
  name: 'Perth',
  x: 10,
  y: 65,
  priority: false
}, {
  id: 'canberra',
  name: 'Canberra',
  x: 68,
  y: 70,
  priority: false
}, {
  id: 'hobart',
  name: 'Hobart',
  x: 55,
  y: 90,
  priority: false
}, {
  id: 'darwin',
  name: 'Darwin',
  x: 35,
  y: 15,
  priority: false
}, {
  id: 'wagga',
  name: 'Wagga Wagga',
  x: 62,
  y: 62,
  priority: true
}];
interface MapProps {
  activeSuburb: string | null;
  onHover: (id: string | null) => void;
}
const D3Map = ({
  activeSuburb,
  onHover
}: MapProps) => {
  const width = 800;
  const height = 600;
  const gridSize = 40;

  // Generate grid lines
  const verticalLines = [];
  const horizontalLines = [];
  for (let i = 0; i <= width; i += gridSize) {
    verticalLines.push(i);
  }
  for (let i = 0; i <= height; i += gridSize) {
    horizontalLines.push(i);
  }

  // Generate river path using SVG path
  const riverPath = `M 0 ${height * 0.45} C ${width * 0.3} ${height * 0.4}, ${width * 0.4} ${height * 0.6}, ${width * 0.5} ${height * 0.55} C ${width * 0.6} ${height * 0.5}, ${width * 0.7} ${height * 0.4}, ${width} ${height * 0.35}`;

  // Generate connection lines between nearby suburbs
  const connectionLines = [];
  SUBURBS.forEach((source, i) => {
    SUBURBS.slice(i + 1).forEach(target => {
      const dist = Math.hypot(source.x - target.x, source.y - target.y);
      if (dist < 25) {
        connectionLines.push({
          x1: source.x * (width / 100),
          y1: source.y * (height / 100),
          x2: target.x * (width / 100),
          y2: target.y * (height / 100)
        });
      }
    });
  });
  return <div data-section-id="38783" className="w-full h-full bg-background relative overflow-hidden">
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid slice" className="w-full h-full block">
        {/* Grid lines */}
        <g className="grid-lines">
          {verticalLines.map(x => <line key={`v-${x}`} x1={x} y1={0} x2={x} y2={height} stroke="var(--border)" strokeWidth={1} opacity={0.2} />)}
          {horizontalLines.map(y => <line key={`h-${y}`} x1={0} y1={y} x2={width} y2={y} stroke="var(--border)" strokeWidth={1} opacity={0.2} />)}
        </g>

        {/* River */}
        <path d={riverPath} fill="none" stroke="var(--ring)" strokeWidth={20} strokeOpacity={0.03} strokeLinecap="round" />

        {/* Connection lines */}
        <g className="links">
          {connectionLines.map((line, idx) => <line key={`link-${idx}`} x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2} stroke="var(--sidebar)" strokeWidth={1} opacity={0.08} />)}
        </g>

        {/* Suburb nodes */}
        <g className="nodes">
          {SUBURBS.map(suburb => {
          const cx = suburb.x * (width / 100);
          const cy = suburb.y * (height / 100);
          const isActive = activeSuburb === suburb.id;
          const radius = isActive ? 10 : suburb.priority ? 6 : 4;
          return <g key={suburb.id} transform={`translate(${cx}, ${cy})`} style={{
            cursor: 'pointer'
          }} onMouseEnter={() => onHover(suburb.id)} onMouseLeave={() => onHover(null)}>
                {/* Pulsing background circle for priority suburbs */}
                {suburb.priority && <circle r={8} fill="var(--ring)" opacity={0.25} className="animate-pulse" />}

                {/* Main node circle */}
                <circle r={radius} fill={suburb.priority ? 'var(--ring)' : 'var(--sidebar)'} stroke="var(--background)" strokeWidth={2} className="transition-all duration-300" />

                {/* Label text */}
                <text dy={-12} textAnchor="middle" fill="var(--sidebar)" fontSize="11px" fontFamily="var(--font-mono)" fontWeight="600" opacity={isActive ? 1 : 0.8} style={{
              pointerEvents: 'none'
            }} className={`transition-all duration-300 ${isActive ? 'font-bold text-sm' : ''}`}>
                  {suburb.name}
                </text>
              </g>;
        })}
        </g>
      </svg>

      {/* Gradient overlay */}
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_center,transparent_20%,var(--background)_130%)]"></div>
    </div>;
};
export default function MelbourneServiceZone() {
  const [activeSuburb, setActiveSuburb] = useState<string | null>(null);
  const {
    ref: panelRef,
    isVisible: isPanelVisible
  } = useRevealOnScroll();
  const priorityZones = SUBURBS.filter(s => s.priority);
  return <section id="service-area" className="relative w-full h-[800px] lg:h-[700px] bg-background overflow-hidden flex flex-col lg:block">
      
      {/* Map background */}
      <div className="absolute inset-0 z-0 h-full w-full">
        <D3Map activeSuburb={activeSuburb} onHover={setActiveSuburb} />
      </div>

      {/* Content panel */}
      <div className="relative z-10 container mx-auto h-full pointer-events-none flex flex-col justify-end lg:justify-center pb-8 lg:pb-0 px-4">
        
        {/* Info panel */}
        <div ref={panelRef} className={`
            pointer-events-auto 
            w-full max-w-md 
            bg-card/95 backdrop-blur-md 
            border border-border 
            rounded-md shadow-xl 
            p-6 lg:p-8
            transition-all duration-700 ease-out
            ${isPanelVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}
          `}>
          <div className="flex items-center gap-2 mb-2 text-ring font-bold tracking-widest uppercase text-[10px] font-mono">
            <MapPin className="size-4" />
            <span>National Distribution Network</span>
          </div>
          
          <h2 className="text-3xl font-bold text-foreground mb-4 tracking-tighter uppercase font-serif">
            Service Coverage
          </h2>
          
          <p className="text-muted-foreground mb-6 leading-relaxed text-sm">
            Brendon J Building Materials operates a high-efficiency wholesale network across Australia. 
            Strategically headquartered in Swan Hill, Victoria, we ensure daily logistics dispatch 
            for bulk building supplies and industrial hardware.
          </p>

          <div className="space-y-4 mb-8">
            <div className="flex items-center justify-between text-xs font-mono font-bold text-foreground border-b border-border pb-2 uppercase">
              <span>Regional Delivery Hubs</span>
              <span className="text-muted-foreground">Daily Routes</span>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {priorityZones.map((zone, i) => <div key={zone.id} data-index={i} onMouseEnter={() => setActiveSuburb(zone.id)} onMouseLeave={() => setActiveSuburb(null)}>
                  <Badge variant={activeSuburb === zone.id ? "default" : "outline"} className={`
                      cursor-pointer transition-all duration-200 px-3 py-1 text-[10px] font-mono uppercase tracking-tight rounded-sm
                      ${activeSuburb === zone.id ? 'bg-ring text-primary-foreground border-ring shadow-md scale-105' : 'border-border text-muted-foreground hover:border-ring hover:text-foreground hover:bg-accent'}
                    `}>
                    {zone.name}
                  </Badge>
                </div>)}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button asChild className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-md rounded-sm font-bold uppercase text-xs tracking-wider">
              <Link to="/ContactPage">
                Request Quote <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="w-full sm:w-auto border-border text-foreground hover:bg-accent rounded-sm font-bold uppercase text-xs tracking-wider">
              <Link to="/ServicesPage">
                Supply Chain
              </Link>
            </Button>
          </div>

          {/* Status indicators */}
          <div className="mt-6 pt-4 border-t border-border flex items-center gap-3 text-[10px] font-mono text-muted-foreground uppercase">
            <div className="flex items-center gap-1.5">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-ring opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-ring"></span>
              </span>
              <span>Logistics Tracking Active</span>
            </div>
            <span className="w-1 h-1 rounded-full bg-border"></span>
            <div className="flex items-center gap-1">
              <Navigation className="size-3" />
              <span>Coverage: Australia Wide</span>
            </div>
          </div>

        </div>
      </div>
    </section>;
}